import React, { useCallback, useEffect, useState } from 'react';
import crmClient from './crmClient';
import { stageLabel } from './format';

const blankCampaign = {
  name: '',
  audience: 'customers',
  filters: {},
  crm_email_template_id: '',
  subject: '',
  body: '',
  sender_name: '',
  sender_email: '',
  reply_to: ''
};

const STATUS_TONES = { draft: 'slate', scheduled: 'blue', sending: 'amber', completed: 'green', cancelled: 'red' };

const CampaignsView = ({ meta }) => {
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(blankCampaign);
  const [selectedId, setSelectedId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stats, setStats] = useState(null);
  const [statsRange, setStatsRange] = useState({ dateFrom: '', dateTo: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([crmClient.listCampaigns(), crmClient.listTemplates()])
      .then(([campaignPayload, templatePayload]) => {
        setCampaigns((campaignPayload && campaignPayload.campaigns) || []);
        setTemplates((templatePayload && templatePayload.templates) || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Unable to load campaigns.');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const audiences = (meta && meta.audiences) || ['customers', 'drivers', 'investors'];
  const stages = (meta && meta.stages) || [];
  const categories = (meta && meta.categories) || {};

  const startNew = () => {
    setSelectedId(null);
    setForm(blankCampaign);
    setPreview(null);
    setNotice(null);
    setError(null);
  };

  const selectCampaign = campaign => {
    setSelectedId(campaign.crm_campaign_id);
    setStatsRange({ dateFrom: '', dateTo: '' });
    setForm({
      name: campaign.name || '',
      audience: campaign.audience || 'customers',
      filters: campaign.filters || {},
      crm_email_template_id: campaign.crm_email_template_id || '',
      subject: campaign.subject || '',
      body: campaign.body || '',
      sender_name: campaign.sender_name || '',
      sender_email: campaign.sender_email || '',
      reply_to: campaign.reply_to || ''
    });
    setPreview(null);
    setNotice(null);
    setError(null);
    setStats(null);
    // Load engagement stats for campaigns that have been sent.
    if (['completed', 'sending'].includes(campaign.status)) {
      loadStats(campaign.crm_campaign_id, { dateFrom: '', dateTo: '' });
    }
  };

  // Fetch campaign stats, optionally scoped to a day/date range for daily performance.
  const loadStats = (id, range) => {
    if (!id) return;
    const params = range && (range.dateFrom || range.dateTo) ? range : undefined;
    crmClient
      .campaignStats(id, params)
      .then(payload => setStats((payload && payload.stats) || null))
      .catch(() => setStats(null));
  };

  const changeStatsRange = (key, value) => {
    const range = { ...statsRange, [key]: value };
    setStatsRange(range);
    loadStats(selectedId, range);
  };

  const applyTemplate = templateId => {
    const template = templates.find(t => t.crm_email_template_id === templateId);
    setForm(prev => ({
      ...prev,
      crm_email_template_id: templateId,
      subject: template && !prev.subject ? template.subject || '' : prev.subject,
      body: template && !prev.body ? template.body || '' : prev.body
    }));
  };

  const setFilter = (key, value) => setForm(prev => ({ ...prev, filters: { ...prev.filters, [key]: value || undefined } }));

  const save = () => {
    setBusy(true);
    setError(null);
    const payload = { ...form, crm_email_template_id: form.crm_email_template_id || null };
    const action = selectedId ? crmClient.updateCampaign(selectedId, payload) : crmClient.createCampaign(payload);
    action
      .then(result => {
        setBusy(false);
        const campaign = result && result.campaign;
        if (campaign) {
          setSelectedId(campaign.crm_campaign_id);
        }
        load();
        setNotice('Saved.');
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to save campaign.');
      });
  };

  const previewAudience = () => {
    if (!selectedId) {
      setError('Save the campaign first to preview its audience.');
      return;
    }
    setBusy(true);
    setError(null);
    crmClient
      .previewCampaign(selectedId)
      .then(result => {
        setBusy(false);
        setPreview(result || { count: 0, sample: [] });
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to preview audience.');
      });
  };

  const send = () => {
    if (!selectedId) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    crmClient
      .sendCampaign(selectedId)
      .then(result => {
        setBusy(false);
        setNotice((result && result.message) || 'Campaign sent.');
        load();
      })
      .catch(err => {
        setBusy(false);
        setError((err && err.response && err.response.data && err.response.data.message) || 'Unable to send campaign.');
      });
  };

  return (
    <div className="crm-campaigns">
      <div className="crm-campaigns-list">
        <div className="crm-calllist-head">
          <h2>Campaigns</h2>
          <button type="button" className="crm-btn crm-btn--sm" onClick={startNew}>
            New
          </button>
        </div>
        {loading && <div className="crm-empty">Loading…</div>}
        {!loading && campaigns.length === 0 && <div className="crm-empty">No campaigns yet.</div>}
        <ul className="crm-campaign-items">
          {campaigns.map(campaign => (
            <li
              key={campaign.crm_campaign_id}
              className={selectedId === campaign.crm_campaign_id ? 'active' : ''}
              onClick={() => selectCampaign(campaign)}
            >
              <strong>{campaign.name}</strong>
              <span className={`crm-stage-pill crm-stage-pill--${STATUS_TONES[campaign.status] || 'slate'}`}>{campaign.status}</span>
              <small>
                {campaign.audience} · {campaign.recipient_count || 0} recipients
              </small>
            </li>
          ))}
        </ul>
      </div>

      <div className="crm-campaign-builder crm-card">
        <h3>{selectedId ? 'Edit campaign' : 'New campaign'}</h3>
        {error && <div className="crm-inline-error">{error}</div>}
        {notice && <div className="crm-preview-banner">{notice}</div>}

        <div className="crm-field-row">
          <label className="crm-field-grow">
            <span>Campaign name</span>
            <input value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} />
          </label>
          <label>
            <span>Audience</span>
            <select value={form.audience} onChange={event => setForm({ ...form, audience: event.target.value })}>
              {audiences.map(a => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="crm-field-row">
          <label>
            <span>Stage filter</span>
            <select value={form.filters.stage || ''} onChange={event => setFilter('stage', event.target.value)}>
              <option value="">Any stage</option>
              {stages.map(stage => (
                <option key={stage} value={stage}>
                  {stageLabel(stage)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Category filter</span>
            <select value={form.filters.category || ''} onChange={event => setFilter('category', event.target.value)}>
              <option value="">Any category</option>
              {Object.keys(categories).map(cat => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Template</span>
            <select value={form.crm_email_template_id || ''} onChange={event => applyTemplate(event.target.value)}>
              <option value="">— none —</option>
              {templates.map(template => (
                <option key={template.crm_email_template_id} value={template.crm_email_template_id}>
                  {template.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="crm-field-grow">
          <span>Subject</span>
          <input value={form.subject} onChange={event => setForm({ ...form, subject: event.target.value })} />
        </label>
        <label className="crm-field-grow">
          <span>Body</span>
          <textarea rows={6} value={form.body} onChange={event => setForm({ ...form, body: event.target.value })} />
        </label>

        <div className="crm-field-row">
          <label>
            <span>Sender name</span>
            <input value={form.sender_name} onChange={event => setForm({ ...form, sender_name: event.target.value })} />
          </label>
          <label>
            <span>Sender email</span>
            <input value={form.sender_email} onChange={event => setForm({ ...form, sender_email: event.target.value })} />
          </label>
          <label>
            <span>Reply-to</span>
            <input value={form.reply_to} onChange={event => setForm({ ...form, reply_to: event.target.value })} />
          </label>
        </div>

        <div className="crm-detail-actions">
          <button type="button" className="crm-btn" disabled={busy || !form.name} onClick={save}>
            {selectedId ? 'Save' : 'Create'}
          </button>
          <button type="button" className="crm-btn crm-btn--ghost" disabled={busy || !selectedId} onClick={previewAudience}>
            Preview audience
          </button>
          <button type="button" className="crm-btn" disabled={busy || !selectedId} onClick={send}>
            Send
          </button>
        </div>

        {stats && (
          <div className="crm-card crm-campaign-stats">
            <h4>Performance</h4>
            <div className="crm-campaign-stats-filter" style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '12px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px' }}>
                <span>From</span>
                <input type="date" value={statsRange.dateFrom} max={statsRange.dateTo || undefined} onChange={event => changeStatsRange('dateFrom', event.target.value)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: '12px' }}>
                <span>To</span>
                <input type="date" value={statsRange.dateTo} min={statsRange.dateFrom || undefined} onChange={event => changeStatsRange('dateTo', event.target.value)} />
              </label>
              {(statsRange.dateFrom || statsRange.dateTo) ? (
                <button type="button" className="crm-btn crm-btn--ghost" onClick={() => { setStatsRange({ dateFrom: '', dateTo: '' }); loadStats(selectedId, { dateFrom: '', dateTo: '' }); }}>Clear</button>
              ) : null}
              <span className="crm-muted" style={{ fontSize: '12px' }}>
                {statsRange.dateFrom || statsRange.dateTo ? 'Showing selected range' : 'All-time — pick a day to see daily performance'}
              </span>
            </div>
            <div className="crm-kpi-grid">
              <div className="crm-kpi"><span>Sent</span><strong>{stats.sent}</strong></div>
              <div className="crm-kpi crm-kpi--blue"><span>Delivered</span><strong>{stats.delivery_rate_pct}%</strong><small>{stats.delivered}</small></div>
              {stats.channel === 'sms' ? (
                <div className="crm-kpi crm-kpi--red"><span>Failed</span><strong>{stats.failure_rate_pct}%</strong><small>{stats.failed} failed</small></div>
              ) : (
                <>
                  <div className="crm-kpi crm-kpi--green"><span>Open rate</span><strong>{stats.open_rate_pct}%</strong><small>{stats.opened} opened</small></div>
                  <div className="crm-kpi crm-kpi--teal"><span>Click rate</span><strong>{stats.click_rate_pct}%</strong><small>{stats.clicked} clicked</small></div>
                  <div className="crm-kpi crm-kpi--red"><span>Bounce rate</span><strong>{stats.bounce_rate_pct}%</strong><small>{stats.bounced} bounced</small></div>
                  <div className="crm-kpi"><span>Unsubscribes</span><strong>{stats.unsubscribed}</strong></div>
                </>
              )}
              <div className="crm-kpi crm-kpi--green"><span>Conversion</span><strong>{stats.conversion_rate_pct || 0}%</strong><small>{stats.converted || 0} of {stats.recipients || 0} ordered</small></div>
            </div>
            {!stats.tracking_active && stats.channel !== 'sms' && (
              <p className="crm-muted">Open and click tracking is built in — rates populate as recipients open and click.</p>
            )}
          </div>
        )}

        {preview && (
          <div className="crm-preview-audience">
            <strong>{preview.count} recipients</strong>
            <ul>
              {(preview.sample || []).map(recipient => (
                <li key={recipient.crm_contact_id}>
                  {[recipient.first_name, recipient.last_name].filter(Boolean).join(' ') || recipient.email} — {recipient.email}
                </li>
              ))}
            </ul>
            {preview.count > (preview.sample || []).length && <small className="crm-muted">Showing first {(preview.sample || []).length}.</small>}
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignsView;
